import { useRef, useCallback } from 'react';

/**
 * Lightweight swipe gesture hook for multi-step dialogs
 * - Swipe left → Next step
 * - Swipe right → Previous step
 * - Ignores vertical scrolling
 * - Threshold: 60px horizontal movement
 */
export function useSwipeSteps({ 
  onNext, 
  onPrev, 
  enabled = true,
  threshold = 60,
  minSteps = 2 
}) {
  const touchStart = useRef({ x: 0, y: 0 });
  const touchDelta = useRef({ x: 0, y: 0 });
  const isSwiping = useRef(false);

  const handleTouchStart = useCallback((e) => {
    if (!enabled) return;
    
    // Don't start swipe if touching a scrollable element that's scrolling
    const target = e.target;
    if (target.closest('[data-no-swipe]')) return;
    
    const touch = e.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
    touchDelta.current = { x: 0, y: 0 };
    isSwiping.current = false;
  }, [enabled]);

  const handleTouchMove = useCallback((e) => {
    if (!enabled) return;
    if (e.target.closest('[data-no-swipe]')) return;

    const touch = e.touches[0];
    touchDelta.current = {
      x: touch.clientX - touchStart.current.x,
      y: touch.clientY - touchStart.current.y
    };

    // Determine if this is a horizontal swipe (must be more horizontal than vertical)
    const absX = Math.abs(touchDelta.current.x);
    const absY = Math.abs(touchDelta.current.y);
    
    if (absX > 10 && absX > absY * 1.5) {
      isSwiping.current = true;
      // Prevent vertical scroll during horizontal swipe
      e.preventDefault();
    }
  }, [enabled]);

  const handleTouchEnd = useCallback((e) => {
    if (!enabled || !isSwiping.current) return;
    
    const deltaX = touchDelta.current.x;
    
    if (Math.abs(deltaX) >= threshold) {
      if (deltaX < 0 && onNext) {
        // Swipe left → Next
        onNext();
      } else if (deltaX > 0 && onPrev) {
        // Swipe right → Previous
        onPrev();
      }
    }
    
    // Reset
    isSwiping.current = false;
    touchDelta.current = { x: 0, y: 0 };
  }, [enabled, threshold, onNext, onPrev]);

  return {
    touchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    isSwiping: isSwiping.current
  };
}

export default useSwipeSteps;
